export type SearchValue = string | number | boolean | null | undefined;

export interface SearchDocument {
  text: SearchValue | SearchValue[];
  fields?: Record<string, SearchValue | SearchValue[]>;
  aliases?: Record<string, string>;
}

interface SearchToken {
  excluded: boolean;
  field?: string;
  value: string;
}

const TOKEN_PATTERN = /-?(?:[^\s:\uff1a"]+)[:\uff1a](?:"[^"]*"|[^\s]+)|-?"[^"]*"|-?[^\s]+/g;
const EXACT_FIELD_NAMES = new Set(['rarity', 'status', 'rank', 'star', 'element']);

export const normalizeSearchText = (value: SearchValue): string => String(value ?? '')
  .normalize('NFKC')
  .toLocaleLowerCase('zh-CN')
  .replace(/[\s\p{P}\p{S}]+/gu, ' ')
  .trim();

const toValues = (value: SearchValue | SearchValue[]): string[] => (Array.isArray(value) ? value : [value])
  .map(normalizeSearchText)
  .filter(Boolean);

export const parseSearchQuery = (query: string): SearchToken[] => {
  const rawTokens = query.normalize('NFKC').match(TOKEN_PATTERN) ?? [];
  return rawTokens.map((raw): SearchToken | undefined => {
    const excluded = raw.startsWith('-');
    const body = excluded ? raw.slice(1) : raw;
    const separator = body.indexOf(':');
    const field = separator > 0 ? normalizeSearchText(body.slice(0, separator)) : undefined;
    const unquoted = (separator > 0 ? body.slice(separator + 1) : body).replace(/^"|"$/g, '');
    const value = normalizeSearchText(unquoted);
    return value ? { excluded, ...(field ? { field } : {}), value } : undefined;
  }).filter((token): token is SearchToken => Boolean(token));
};

export const prepareSearchDocument = (document: SearchDocument) => {
  const fields = Object.fromEntries(Object.entries(document.fields ?? {}).map(([key, value]) => [normalizeSearchText(key), toValues(value)]));
  const aliases = Object.fromEntries(Object.entries(document.aliases ?? {}).map(([key, value]) => [normalizeSearchText(key), normalizeSearchText(value)]));
  const allValues = [...toValues(document.text), ...Object.values(fields).flat()];
  return { fields, aliases, allValues };
};

/** Normalize stable catalog data once; compile the query once per edit. */
export const compileSearchQuery = (query: string) => {
  const tokens = parseSearchQuery(query);
  return ({ fields, aliases, allValues }: ReturnType<typeof prepareSearchDocument>): boolean => {
  const tokenMatches = (token: SearchToken): boolean => {
    const field = token.field ? aliases[token.field] ?? token.field : undefined;
    const candidates = field ? fields[field] ?? [] : allValues;
    const exact = field ? EXACT_FIELD_NAMES.has(field) : false;
    return candidates.some((candidate) => exact ? candidate === token.value : candidate.includes(token.value));
  };
  return tokens.every((token) => token.excluded ? !tokenMatches(token) : tokenMatches(token));
  };
};

export const matchesAdvancedSearch = (query: string, document: SearchDocument): boolean => {
  if (!query.trim()) return true;
  return compileSearchQuery(query)(prepareSearchDocument(document));
};
